import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Overline } from "./Reveal";
import { submitMembership, submitVendor } from "../lib/api";
import { Check } from "lucide-react";

const inputCls =
  "w-full bg-transparent border-0 border-b border-navy/25 rounded-none px-0 py-3 font-sans font-light text-navy text-base placeholder:text-navyAccent/40 focus:outline-none focus:border-gold transition-colors duration-300";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[0.65rem] tracking-[0.2em] uppercase text-navyAccent/60 font-sans">
      {label}
    </span>
    <div className="mt-1">{children}</div>
  </label>
);

const Success = ({ title, sub }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex flex-col items-start gap-5 py-10"
    data-testid="form-success"
  >
    <span className="flex h-14 w-14 items-center justify-center rounded-full border border-gold text-gold">
      <Check size={26} strokeWidth={1.5} />
    </span>
    <h3 className="font-serif font-light text-navy text-3xl md:text-4xl tracking-tight">
      {title}
    </h3>
    <p className="font-sans font-light text-navyAccent/80 max-w-md leading-relaxed">
      {sub}
    </p>
  </motion.div>
);

const MembershipForm = ({ selectedTier }) => {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    location: "",
    invite_code: "",
    tier: selectedTier || "",
    referral: "",
    message: "",
  });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  // sync tier from selection
  useEffect(() => {
    if (selectedTier) setForm((f) => ({ ...f, tier: selectedTier }));
  }, [selectedTier]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setLoading(true);
    try {
      await submitMembership({ ...form, tier: form.tier || selectedTier || "" });
      setDone(true);
      toast.success("Your application has been received.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <Success
        title="Received with discretion."
        sub="Thank you. A member of our committee will review your application and reach out privately. We correspond only with those we can serve."
      />
    );

  return (
    <form onSubmit={submit} data-testid="membership-form" className="space-y-8">
      <div className="grid sm:grid-cols-2 gap-8">
        <Field label="Full Name">
          <input
            data-testid="member-name-input"
            className={inputCls}
            value={form.full_name}
            onChange={update("full_name")}
            placeholder="Your name"
          />
        </Field>
        <Field label="Email">
          <input
            data-testid="member-email-input"
            type="email"
            className={inputCls}
            value={form.email}
            onChange={update("email")}
            placeholder="you@domain.com"
          />
        </Field>
        <Field label="Phone">
          <input
            data-testid="member-phone-input"
            className={inputCls}
            value={form.phone}
            onChange={update("phone")}
            placeholder="+00 000 000 000"
          />
        </Field>
        <Field label="Location">
          <input
            data-testid="member-location-input"
            className={inputCls}
            value={form.location}
            onChange={update("location")}
            placeholder="City, Country"
          />
        </Field>
        <Field label="Preferred Tier">
          <select
            data-testid="member-tier-select"
            className={inputCls + " cursor-pointer"}
            value={form.tier}
            onChange={update("tier")}
          >
            <option value="">Select a tier</option>
            <option value="Signature">Signature — ₹2,00,000 / yr</option>
            <option value="Noir">Noir · Invite Only — ₹3,00,000 / yr</option>
          </select>
        </Field>
        <Field label="Invitation Code">
          <input
            data-testid="member-invite-input"
            className={inputCls}
            value={form.invite_code}
            onChange={update("invite_code")}
            placeholder="If you have one"
          />
        </Field>
      </div>
      <Field label="Who referred you?">
        <input
          data-testid="member-referral-input"
          className={inputCls}
          value={form.referral}
          onChange={update("referral")}
          placeholder="Name of the member who invited you"
        />
      </Field>
      <Field label="Tell us how we can serve you">
        <textarea
          data-testid="member-message-input"
          rows={3}
          className={inputCls + " resize-none"}
          value={form.message}
          onChange={update("message")}
          placeholder="Your lifestyle, your expectations..."
        />
      </Field>
      <button
        type="submit"
        disabled={loading}
        data-testid="member-submit-btn"
        className="group inline-flex items-center gap-3 rounded-full bg-navy px-9 py-4 text-xs uppercase tracking-[0.18em] text-ivory hover:bg-navyAccent transition-colors duration-300 disabled:opacity-60"
      >
        {loading ? "Submitting..." : "Submit Application"}
        <span className="h-1.5 w-1.5 rounded-full bg-gold transition-transform duration-300 group-hover:scale-150" />
      </button>
    </form>
  );
};

const VendorForm = () => {
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    category: "",
    website: "",
    region: "",
    message: "",
  });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email) {
      toast.error("Please complete company, contact and email.");
      return;
    }
    setLoading(true);
    try {
      await submitVendor(form);
      setDone(true);
      toast.success("Your partnership request has been received.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <Success
        title="Thank you, partner."
        sub="We are always seeking exceptional houses and suppliers. Our partnerships team will be in touch to explore a collaboration."
      />
    );

  return (
    <form onSubmit={submit} data-testid="vendor-form" className="space-y-8">
      <div className="grid sm:grid-cols-2 gap-8">
        <Field label="Company / House">
          <input
            data-testid="vendor-company-input"
            className={inputCls}
            value={form.company_name}
            onChange={update("company_name")}
            placeholder="Your brand"
          />
        </Field>
        <Field label="Contact Name">
          <input
            data-testid="vendor-contact-input"
            className={inputCls}
            value={form.contact_name}
            onChange={update("contact_name")}
            placeholder="Your name"
          />
        </Field>
        <Field label="Email">
          <input
            data-testid="vendor-email-input"
            type="email"
            className={inputCls}
            value={form.email}
            onChange={update("email")}
            placeholder="you@company.com"
          />
        </Field>
        <Field label="Phone">
          <input
            data-testid="vendor-phone-input"
            className={inputCls}
            value={form.phone}
            onChange={update("phone")}
            placeholder="+00 000 000 000"
          />
        </Field>
        <Field label="Category">
          <select
            data-testid="vendor-category-select"
            className={inputCls + " cursor-pointer"}
            value={form.category}
            onChange={update("category")}
          >
            <option value="">Select a category</option>
            <option value="Hospitality">Hospitality & Dining</option>
            <option value="Travel">Travel & Aviation</option>
            <option value="Retail">Luxury Retail & Goods</option>
            <option value="Events">Events & Entertainment</option>
            <option value="Other">Other</option>
          </select>
        </Field>
        <Field label="Region Served">
          <input
            data-testid="vendor-region-input"
            className={inputCls}
            value={form.region}
            onChange={update("region")}
            placeholder="e.g. Europe, Global"
          />
        </Field>
      </div>
      <Field label="Website">
        <input
          data-testid="vendor-website-input"
          className={inputCls}
          value={form.website}
          onChange={update("website")}
          placeholder="https://"
        />
      </Field>
      <Field label="Tell us about your offering">
        <textarea
          data-testid="vendor-message-input"
          rows={3}
          className={inputCls + " resize-none"}
          value={form.message}
          onChange={update("message")}
          placeholder="What makes your house exceptional..."
        />
      </Field>
      <button
        type="submit"
        disabled={loading}
        data-testid="vendor-submit-btn"
        className="group inline-flex items-center gap-3 rounded-full bg-navy px-9 py-4 text-xs uppercase tracking-[0.18em] text-ivory hover:bg-navyAccent transition-colors duration-300 disabled:opacity-60"
      >
        {loading ? "Submitting..." : "Become a Partner"}
        <span className="h-1.5 w-1.5 rounded-full bg-gold transition-transform duration-300 group-hover:scale-150" />
      </button>
    </form>
  );
};

export const ApplyForms = ({ selectedTier }) => {
  const [tab, setTab] = useState("member");

  return (
    <section
      id="apply"
      data-testid="apply-section"
      className="relative bg-ivory py-28 md:py-40 scroll-mt-24"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 lg:gap-20">
        {/* Sticky left */}
        <div className="md:col-span-4">
          <div className="md:sticky md:top-28">
            <Overline>{tab === "member" ? "Request an Invitation" : "Partner With Us"}</Overline>
            <h2 className="mt-6 font-serif font-light text-navy text-4xl md:text-5xl leading-[1.02] tracking-tight">
              {tab === "member" ? (
                <>
                  Begin your
                  <br />
                  <span className="italic text-gold">private application.</span>
                </>
              ) : (
                <>
                  Join our
                  <br />
                  <span className="italic text-gold">network of houses.</span>
                </>
              )}
            </h2>
            <p className="mt-6 font-sans font-light text-navyAccent/80 leading-relaxed max-w-sm">
              {tab === "member"
                ? "Applications are reviewed personally and in confidence. There is no obligation, and every submission is treated with discretion."
                : "We collaborate with a curated circle of vendors, suppliers and experience-makers worldwide."}
            </p>

            <div
              className="mt-10 inline-flex p-1 border border-navy/15 rounded-full bg-beige"
              data-testid="apply-tabs"
            >
              <button
                data-testid="tab-member"
                onClick={() => setTab("member")}
                className={`rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                  tab === "member" ? "bg-navy text-ivory" : "text-navyAccent"
                }`}
              >
                Membership
              </button>
              <button
                data-testid="tab-vendor"
                onClick={() => setTab("vendor")}
                className={`rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                  tab === "vendor" ? "bg-navy text-ivory" : "text-navyAccent"
                }`}
              >
                Vendor / Partner
              </button>
            </div>
          </div>
        </div>

        {/* Form right */}
        <div className="md:col-span-7 md:col-start-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
            >
              {tab === "member" ? (
                <MembershipForm selectedTier={selectedTier} />
              ) : (
                <VendorForm />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
